import { arrayMove } from "@dnd-kit/sortable";
import { Filter, MaximizeIcon, X } from "lucide-react";
import {
  FC,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ChronologyEvent } from "~/api/model";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { MarkdownExportButton } from "~/components/MarkdownExportButton";
import { TabControlBar } from "~/components/TabControlBar";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { formatCaseInfo } from "~/features/chat/helpers";
import { useChatDocumentParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { useToast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";
import {
  ChronologyFormValues,
  formValuesToCreateRequest,
} from "../helpers/chronologyRequest";
import type { ChronologyDuplicateGroup } from "../helpers/detectDuplicateEvents";
import { formatChronologyAsMarkdown } from "../helpers/formatChronologyAsMarkdown";
import { formatChronologyAsPlainText } from "../helpers/formatChronologyAsPlainText";
import {
  useChronologyEvents,
  useChronologyMutations,
} from "../hooks/useChronology";
import { useDetectDuplicateGroups } from "../hooks/useDetectDuplicateGroups";
import { useGenerateChronology } from "../hooks/useGenerateChronology";
import { useChronologyStore } from "../stores/useChronologyStore";
import type { ChronologyDuplicateRowInfo } from "./ChronologyDuplicateDot";
import { ChronologyDuplicatesModal } from "./ChronologyDuplicatesModal";
import { ChronologyEventEditorModal } from "./ChronologyEventEditorModal";
import { ChronologyList } from "./ChronologyList";
import { GenerateChronologyView } from "./GenerateChronologyView";

const SKELETON_ROW_COUNT = 4;

// A group needs at least two members, so the reviewed group is only still alive
// when at least this many of its original ids survive together. A lone shared id
// can belong to an unrelated group that only formed once a blocking member was
// deleted, so we close the modal rather than silently switch to it.
const MIN_REVIEWED_GROUP_OVERLAP = 2;

const matchesFilter = (event: ChronologyEvent, term: string): boolean => {
  const lower = term.toLowerCase();
  return (
    event.detail.toLowerCase().includes(lower) ||
    event.eventType.toLowerCase().includes(lower) ||
    event.provider.toLowerCase().includes(lower) ||
    event.facility.toLowerCase().includes(lower) ||
    event.documentCategory.toLowerCase().includes(lower) ||
    event.documentName.toLowerCase().includes(lower) ||
    (event.eventDate ?? "").toLowerCase().includes(lower) ||
    event.dateText.toLowerCase().includes(lower) ||
    (event.reference.batesNumber ?? "").toLowerCase().includes(lower) ||
    String(event.reference.pageNumber ?? "").includes(lower)
  );
};

/**
 * The Chronology tab: a date-sorted, editable list of a case's chronology
 * events. Owns the load/empty/error states and the "Add Event" flow; the row
 * and editor components handle per-event editing.
 */
export const ChronologyArea: FC = () => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const { toast } = useToast();
  const { isMobile } = useMobile();
  const { organizationId } = useChatDocumentParams();
  const { events, isLoading, isError, error, refetch } = useChronologyEvents();
  const { create, isCreating } = useChronologyMutations();
  const { canGenerate, isGenerating, generate } = useGenerateChronology();
  const editor = useChronologyStore((state) => state.editor);
  const closeEditor = useChronologyStore((state) => state.closeEditor);
  const [maximizeModalOpen, setMaximizeModalOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);

  const { project } = useParentCase();
  const caseInfo = project ? formatCaseInfo(project) : undefined;

  const isContentEmpty = events.length === 0;
  const exportFileName = formatMessage({
    id: "common.chronology",
    defaultMessage: "Chronology",
  });

  const isAdding = editor.mode === "adding";
  const showGenerateView =
    !isLoading &&
    !isError &&
    events.length === 0 &&
    !isAdding &&
    canGenerate &&
    !isGenerating;
  const showGeneratingSkeleton = isGenerating && events.length === 0;

  const duplicateGroups = useDetectDuplicateGroups(events);

  // Map every flagged event id to its group's color, size, and members so each
  // row can render a dot that ties it to its siblings and reopens the review
  // modal scoped to just that group.
  const duplicateInfoById = useMemo(() => {
    const map = new Map<string, ChronologyDuplicateRowInfo>();
    duplicateGroups.forEach((group, index) => {
      const eventIds = group.events.map((event) => event.id);
      for (const id of eventIds) {
        map.set(id, { colorIndex: index, count: eventIds.length, eventIds });
      }
    });
    return map;
  }, [duplicateGroups]);

  // The clicked group is tracked by its member ids rather than a group id, so
  // it survives recompute as members are deleted: the modal keeps showing the
  // shrinking group and closes once fewer than two of its original ids remain
  // grouped together.
  const [reviewedEventIds, setReviewedEventIds] = useState<string[] | null>(
    null
  );
  const reviewedGroup = useMemo<ChronologyDuplicateGroup | null>(() => {
    if (!reviewedEventIds) return null;
    const reviewed = new Set(reviewedEventIds);
    let best: ChronologyDuplicateGroup | null = null;
    let bestOverlap = 0;
    for (const group of duplicateGroups) {
      const overlap = group.events.reduce(
        (total, event) => (reviewed.has(event.id) ? total + 1 : total),
        0
      );
      if (overlap > bestOverlap) {
        best = group;
        bestOverlap = overlap;
      }
    }
    return bestOverlap >= MIN_REVIEWED_GROUP_OVERLAP ? best : null;
  }, [duplicateGroups, reviewedEventIds]);

  const handleReviewDuplicates = useCallback((groupEventIds: string[]) => {
    trackEvent({ eventName: "app/chronology/duplicate_dot.button/click" });
    setReviewedEventIds(groupEventIds);
  }, []);

  useEffect(() => {
    if (
      editor.mode === "editing" &&
      !isLoading &&
      !events.some((event) => event.id === editor.eventId)
    ) {
      closeEditor();
    }
  }, [editor, events, isLoading, closeEditor]);

  const eventIds = useMemo(() => events.map((e) => e.id).join(","), [events]);
  useEffect(() => {
    setLocalOrder(null);
  }, [eventIds]);

  const orderedEvents = useMemo(() => {
    if (!localOrder) return events;
    const eventMap = new Map(events.map((e) => [e.id, e]));
    const localOrderSet = new Set(localOrder);
    const ordered: ChronologyEvent[] = [];
    for (const id of localOrder) {
      const event = eventMap.get(id);
      if (event) ordered.push(event);
    }
    for (const event of events) {
      if (!localOrderSet.has(event.id)) ordered.push(event);
    }
    return ordered;
  }, [events, localOrder]);

  const markdownContent = useMemo(
    () => formatChronologyAsMarkdown(orderedEvents),
    [orderedEvents]
  );

  const plainTextContent = useMemo(
    () => formatChronologyAsPlainText(orderedEvents),
    [orderedEvents]
  );

  const editingEventId = editor.mode === "editing" ? editor.eventId : undefined;

  const deferredFilter = useDeferredValue(filterText);

  const filteredEvents = useMemo(() => {
    if (!deferredFilter.trim()) return orderedEvents;
    return orderedEvents.filter(
      (e) => e.id === editingEventId || matchesFilter(e, deferredFilter.trim())
    );
  }, [orderedEvents, deferredFilter, editingEventId]);

  const handleGenerate = async () => {
    try {
      const result = await generate();
      if (result?.partialFailureCount) {
        toast({
          variant: "destructive",
          title: intl.formatMessage(
            { id: "chronology.generate.partialError" },
            { count: result.partialFailureCount }
          ),
        });
      }
    } catch (generateError) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "chronology.generate.error" }),
        description:
          generateError instanceof Error
            ? generateError.message
            : intl.formatMessage({ id: "chronology.error.unknown" }),
      });
    }
  };

  const handleCreate = async (values: ChronologyFormValues) => {
    try {
      await create(formValuesToCreateRequest(values));
      closeEditor();
    } catch (createError) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "chronology.error.create" }),
        description:
          createError instanceof Error
            ? createError.message
            : intl.formatMessage({ id: "chronology.error.unknown" }),
      });
    }
  };

  const handleReorder = (activeId: string, overId: string) => {
    const currentIds = orderedEvents.map((e) => e.id);
    const oldIndex = currentIds.indexOf(activeId);
    const newIndex = currentIds.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) return;
    setLocalOrder(arrayMove(currentIds, oldIndex, newIndex));
    trackEvent({ eventName: "app/chronology/event.drag/reorder" });
  };

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden",
        isMobile ? "h-docsidebar-content-mobile" : "h-[calc(100vh-60px)]"
      )}
    >
      {!showGeneratingSkeleton && (
        <div className="flex shrink-0 items-center gap-1 border-b border-border px-3 py-2">
          <div className="flex w-full grow">
            <div className="relative flex flex-1 items-center">
              <Input
                placeholder={intl.formatMessage({
                  id: "common.filter",
                  defaultMessage: "Filter",
                })}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="pl-8"
                parentClassName="flex items-center w-full"
                iconBefore={
                  <Filter className="h-4 w-4 shrink-0 text-muted-foreground" />
                }
              />
              {filterText && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 hover:bg-transparent"
                  onClick={() => setFilterText("")}
                  aria-label={intl.formatMessage({
                    id: "common.clearFilter",
                    defaultMessage: "Clear filter",
                  })}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {showGeneratingSkeleton ? (
        <div className="flex-1 min-h-0 p-3">
          <div className="flex flex-col gap-1.5">
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
              <Skeleton key={index} className="h-14 w-full rounded-md" />
            ))}
          </div>
        </div>
      ) : showGenerateView ? (
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-3">
          <div className="px-3 flex items-center justify-center h-full">
            <GenerateChronologyView
              onGenerate={handleGenerate}
              isGenerating={false}
            />
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex-1 min-h-0 p-3">
          <div className="flex flex-col gap-1.5">
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
              <Skeleton key={index} className="h-14 w-full rounded-md" />
            ))}
          </div>
        </div>
      ) : isError ? (
        <div className="flex-1 min-h-0 p-3">
          <div className="flex flex-col items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-4">
            <p className="text-sm font-medium text-destructive">
              <FormattedMessage
                id="chronology.error.load"
                defaultMessage="We couldn't load the chronology."
              />
            </p>
            {error instanceof Error && (
              <p className="text-xs text-muted-foreground">{error.message}</p>
            )}
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <FormattedMessage
                id="chronology.action.retry"
                defaultMessage="Try Again"
              />
            </Button>
          </div>
        </div>
      ) : events.length === 0 ? (
        <div className="flex-1 min-h-0 p-3">
          <div className="flex flex-col items-center gap-1 py-16 text-center">
            <p className="text-sm font-medium">
              <FormattedMessage
                id="chronology.empty.title"
                defaultMessage="Nothing here yet"
              />
            </p>
            <p className="max-w-xs text-sm text-muted-foreground">
              <FormattedMessage
                id="chronology.empty.description"
                defaultMessage="Events will show up here as we read the case — or add one yourself."
              />
            </p>
          </div>
        </div>
      ) : (
        <ChronologyList
          events={filteredEvents}
          onReorder={handleReorder}
          duplicateInfoById={duplicateInfoById}
          onReviewDuplicates={handleReviewDuplicates}
        />
      )}

      <TabControlBar className={isMobile ? "shrink-0" : ""}>
        <TabControlBar.Group>
          <Button
            variant="ghost"
            size="mini"
            disabled={isContentEmpty}
            onClick={() => {
              trackEvent({
                eventName: "app/chronology/maximize.button/click",
              });
              setMaximizeModalOpen(true);
            }}
          >
            <MaximizeIcon className="w-4 h-4 mr-1" />
            <FormattedMessage id="common.maximize" defaultMessage="Maximize" />
          </Button>
          <ContentCopyButton
            content={plainTextContent}
            size="mini"
            preCopyMessageId="copy"
            eventName="app/chronology/copy_all.button/click"
            disabled={isContentEmpty}
          />
          <MarkdownExportButton
            organizationId={organizationId}
            content={markdownContent}
            fileName={exportFileName}
            buttonSize="mini"
            openEventName="app/chronology/export.button/click"
            exportEventName="app/chronology/export_menu_open/export_action.link/click"
            disabled={isContentEmpty}
            title={exportFileName}
            caseInfo={caseInfo}
          />
        </TabControlBar.Group>
      </TabControlBar>

      <ChronologyEventEditorModal
        open={isAdding}
        onOpenChange={(open) => {
          if (!open) closeEditor();
        }}
        mode="add"
        isSaving={isCreating}
        onSubmit={handleCreate}
      />

      {maximizeModalOpen && (
        <MarkdownContentModal
          title={formatMessage({
            id: "common.chronology",
            defaultMessage: "Chronology",
          })}
          filename={exportFileName}
          content={markdownContent}
          open={maximizeModalOpen}
          onOpenChange={setMaximizeModalOpen}
          copyEventName="app/chronology_maximize_modal/copy_all.button/click"
          exportOpenEventName="app/chronology_maximize_modal/export.button/click"
          exportEventName="app/chronology_maximize_modal/export_menu_open/export_action.link/click"
          terms={[]}
          caseInfo={caseInfo}
        />
      )}

      <ChronologyDuplicatesModal
        open={reviewedGroup !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setReviewedEventIds(null);
        }}
        group={reviewedGroup}
      />
    </div>
  );
};
